import { Course, Enrollment, Tag, TagCategoryWithTags } from '../types';
import { CatalogBrandingConfig } from './catalogGrouping';

/**
 * Catalogue SCOPE — the axis above the shelves.
 *
 * A tenant's tag categories do three different jobs, separated only by which
 * slot of `branding.catalog` names them:
 *
 *   scopeBy   partitions the catalogue  — you are in exactly one at a time
 *   groupBy   arranges what's in scope  — shelves
 *   pivotBy   sub-groups each shelf     — rows, and the pivot toggle
 *
 * Scope is not a filter. A filter is optional, off by default and cleared
 * every visit; a scope is on by default, remembered, and everything else on
 * the page is measured inside it. Revise uses it for school curriculum (CAPS
 * vs IEB): the same Lab and Paper vocabulary means an unscoped catalogue puts
 * two curricula in one shelf on cards that look identical.
 *
 * `scopeBy` is an ORDERED LIST, outermost first, even while it holds a single
 * entry — so adding Subject or Grade later is a settings edit, not a code
 * change. Everything here is generic over N axes for that reason.
 *
 * Design + decision record: the "Curriculum Switch" wireframes.
 */

/** One resolved scope axis: a tag category whose tags partition the catalogue. */
export interface ScopeAxis {
  category: TagCategoryWithTags;
  /** The axis's selectable values, in the tenant's own tag order. */
  tags: Tag[];
  /** Tag named by `scopeDefault`, used before a student has chosen. */
  defaultTag: Tag | null;
}

/** The chosen value per axis, keyed by tag-category id. */
export type ScopeSelection = Record<string, Tag>;

/**
 * Resolve `branding.catalog.scopeBy` against the tenant's live tag categories.
 *
 * A named category that doesn't exist, or exists with no tags, is dropped
 * rather than producing an axis nothing can satisfy — a broken config must
 * degrade to the unscoped catalogue, never to an empty one. Same rule
 * `resolveGroupingConfig` follows for the shelves.
 */
export function resolveScopeAxes(
  branding: CatalogBrandingConfig | undefined | null,
  categories: TagCategoryWithTags[],
): ScopeAxis[] {
  const names = branding?.scopeBy;
  if (!names || names.length === 0) return [];

  const axes: ScopeAxis[] = [];
  for (const name of names) {
    const category = categories.find((c) => c.name === name);
    if (!category || category.tags.length === 0) continue;
    // `scopeDefault` is a single value today because there is a single axis.
    // When a second axis lands it becomes a map; until then, only apply the
    // named default to an axis that actually carries a tag by that name.
    const defaultTag =
      category.tags.find((t) => t.name === branding?.scopeDefault) ?? null;
    axes.push({ category, tags: category.tags, defaultTag });
  }
  return axes;
}

/** Look up an axis value by tag id or (case-insensitive) tag name. */
export function findScopeTag(axis: ScopeAxis, value: string | null | undefined): Tag | null {
  if (!value) return null;
  const lower = value.toLowerCase();
  return (
    axis.tags.find((t) => t.id === value) ??
    axis.tags.find((t) => t.name.toLowerCase() === lower) ??
    null
  );
}

/** The course's tag on this axis, or null when it carries none. */
export function courseScopeTag(course: Pick<Course, 'tags'>, axis: ScopeAxis): Tag | null {
  return (course.tags ?? []).find((t) => t.categoryId === axis.category.id) ?? null;
}

/**
 * Is this course visible under the current scope?
 *
 * Three rules, and the two exemptions are the whole safety story:
 *
 * 1. A course is excluded ONLY by carrying a DIFFERENT axis value. Absence is
 *    never exclusion — an untagged course (exam technique, study skills, or
 *    simply one somebody forgot to tag) shows in EVERY scope rather than none.
 *    The grouped catalogue already works this way, collecting untagged courses
 *    into a trailing group so a missing tag can't make a course vanish.
 *
 * 2. A course the student is enrolled in is ALWAYS visible. There is no
 *    "show everything" escape hatch by design, so without this rule switching
 *    curriculum would hide something they had already paid for and started,
 *    with no way to get back to it.
 *
 * 3. No selection on an axis means that axis constrains nothing.
 */
export function isCourseInScope(
  course: Pick<Course, 'id' | 'tags'>,
  axes: ScopeAxis[],
  selection: ScopeSelection,
  ownedCourseIds: ReadonlySet<string>,
): boolean {
  if (ownedCourseIds.has(course.id)) return true;
  return axes.every((axis) => {
    const chosen = selection[axis.category.id];
    if (!chosen) return true;
    const own = courseScopeTag(course, axis);
    return own === null || own.id === chosen.id;
  });
}

/** Narrow a course list to the current scope. Returns the input when unscoped. */
export function applyScope<T extends Pick<Course, 'id' | 'tags'>>(
  courses: T[],
  axes: ScopeAxis[],
  selection: ScopeSelection,
  ownedCourseIds: ReadonlySet<string> = new Set(),
): T[] {
  if (axes.length === 0 || Object.keys(selection).length === 0) return courses;
  return courses.filter((c) => isCourseInScope(c, axes, selection, ownedCourseIds));
}

/**
 * Which value has this student already told us, by enrolling?
 *
 * Someone with courses on one curriculum has answered the question, and asking
 * again is asking something we know. Returns the axis value carried by most of
 * their enrolments; null on a tie or when none of them carry one, so the
 * caller falls through to the tenant default rather than guessing.
 */
export function inferScopeFromEnrollments(
  courses: Pick<Course, 'id' | 'tags'>[],
  enrollments: Pick<Enrollment, 'courseId'>[],
  axis: ScopeAxis,
): Tag | null {
  const enrolled = new Set(enrollments.map((e) => e.courseId));
  const tally = new Map<string, { tag: Tag; n: number }>();

  for (const course of courses) {
    if (!enrolled.has(course.id)) continue;
    const tag = courseScopeTag(course, axis);
    if (!tag) continue;
    const seen = tally.get(tag.id);
    if (seen) seen.n += 1;
    else tally.set(tag.id, { tag, n: 1 });
  }

  let best: { tag: Tag; n: number } | null = null;
  let tied = false;
  for (const entry of tally.values()) {
    if (!best || entry.n > best.n) {
      best = entry;
      tied = false;
    } else if (entry.n === best.n) {
      tied = true;
    }
  }
  return best && !tied ? best.tag : null;
}

/**
 * The resolution order for one axis, and the ONLY statement of it:
 * explicit choice -> what their enrolments say -> the tenant default.
 *
 * `ScopeContext` calls this rather than re-deriving the order inline. That
 * matters — an earlier version reimplemented it across two pieces of state and
 * a seeding effect quietly overwrote the inferred value, which no test could
 * catch because the tested resolver wasn't the one running.
 *
 * Takes the inferred tag rather than computing it, because inference needs the
 * course list and the caller is the one that has it.
 *
 * Deliberately never falls back to "the first tag": that silently changes
 * meaning the moment somebody reorders the tags in admin settings. An axis
 * with no default and no signal stays unset, which leaves it unconstrained.
 */
export function resolveScopeValue(
  axis: ScopeAxis,
  explicit: string | null | undefined,
  inferred: Tag | null = null,
): Tag | null {
  return findScopeTag(axis, explicit) ?? inferred ?? axis.defaultTag;
}

/**
 * A bundle's scope values, DERIVED from the courses inside it.
 *
 * Bundles carry no tags of their own — the same gap that makes every shelf
 * suspend the bundle-dedupe rule under a tag filter. Rather than give them a
 * tag join table they have never had, a pack takes its curriculum from its
 * members: all-CAPS members make a CAPS pack, and a pack spanning both shows
 * in both (which is the honest answer for a pack that genuinely spans them).
 *
 * Members the reader can't see (an unpublished course) simply aren't in the
 * course list and contribute nothing — the same "drop the NULL join, never map
 * it" rule the bundle mappers already follow.
 */
export function bundleScopeTagIds(
  bundle: { courses: { id: string }[] },
  courseById: ReadonlyMap<string, Pick<Course, 'id' | 'tags'>>,
  axis: ScopeAxis,
): Set<string> {
  const ids = new Set<string>();
  for (const member of bundle.courses) {
    const course = courseById.get(member.id);
    if (!course) continue;
    const tag = courseScopeTag(course, axis);
    if (tag) ids.add(tag.id);
  }
  return ids;
}

/**
 * Same three rules as a course: excluded only by being wholly OTHER, always
 * visible once owned, unconstrained on an axis with no selection.
 */
export function isBundleInScope(
  bundle: { id: string; courses: { id: string }[] },
  courseById: ReadonlyMap<string, Pick<Course, 'id' | 'tags'>>,
  axes: ScopeAxis[],
  selection: ScopeSelection,
  ownedBundleIds: ReadonlySet<string>,
): boolean {
  if (ownedBundleIds.has(bundle.id)) return true;
  return axes.every((axis) => {
    const chosen = selection[axis.category.id];
    if (!chosen) return true;
    const own = bundleScopeTagIds(bundle, courseById, axis);
    return own.size === 0 || own.has(chosen.id);
  });
}

/** Narrow a bundle list to the current scope. */
export function applyScopeToBundles<T extends { id: string; courses: { id: string }[] }>(
  bundles: T[],
  courses: Pick<Course, 'id' | 'tags'>[],
  axes: ScopeAxis[],
  selection: ScopeSelection,
  ownedBundleIds: ReadonlySet<string> = new Set(),
): T[] {
  if (axes.length === 0 || Object.keys(selection).length === 0) return bundles;
  const courseById = new Map(courses.map((c) => [c.id, c]));
  return bundles.filter((b) => isBundleInScope(b, courseById, axes, selection, ownedBundleIds));
}

/**
 * How many courses each axis value carries, for the numbers beside the switch.
 *
 * An untagged course belongs to every value, so it counts under each — the
 * number next to "IEB" has to be what the student will actually find there.
 */
export function scopeCounts(
  courses: Pick<Course, 'id' | 'tags'>[],
  axis: ScopeAxis,
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const tag of axis.tags) counts[tag.id] = 0;
  for (const course of courses) {
    const tag = courseScopeTag(course, axis);
    if (tag) counts[tag.id] = (counts[tag.id] ?? 0) + 1;
    else for (const t of axis.tags) counts[t.id] += 1;
  }
  return counts;
}

/**
 * How many courses actually CARRY each value — "does this curriculum exist
 * yet?", which is a different question from "what would I find in it?".
 *
 * The switch offers a value on this count, not `scopeCounts`. Sharing one
 * number would mean a single untagged course made every curriculum look
 * populated: publish one general study-skills course and IEB starts being
 * offered before a single IEB course exists, landing whoever picks it on that
 * one course and nothing else.
 */
export function scopeTaggedCounts(
  courses: Pick<Course, 'id' | 'tags'>[],
  axis: ScopeAxis,
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const tag of axis.tags) counts[tag.id] = 0;
  for (const course of courses) {
    const tag = courseScopeTag(course, axis);
    if (tag) counts[tag.id] = (counts[tag.id] ?? 0) + 1;
  }
  return counts;
}
